import api from './api';
import firstPartyAnalytics from './firstPartyAnalytics';
import { AnalyticsEvents } from './analyticsEvents';
import { t } from '../i18n';

// ===================================================================
// Typen (alle Felder optional/nullable - defensives Parsing!)
// Backend: dartcloud-backend/src/core/schemas/chat-schema.js
// ===================================================================

export type ChatEntryType = 'TOURNAMENT' | 'CLUB' | 'PUB' | 'SHOP' | 'SERIES' | 'LEAGUE' | 'ASSOCIATION' | 'USER';

export interface ChatMessage {
  id?: string | null;
  senderId?: string | null;
  senderName?: string | null;
  content?: string | null;
  timestamp?: number | null;
  read?: boolean | null;
  readAt?: number | null;
  [key: string]: any;
}

export interface Chat {
  id?: string | null;
  createdAt?: number | null;
  updatedAt?: number | null;

  // Verknüpfung zum Eintrag (Turnier, Verein, Spielstätte, Shop, Serie)
  entryId?: string | null;
  entryType?: string | null;
  entryName?: string | null;

  // Teilnehmer
  ownerId?: string | null;
  ownerName?: string | null;
  userId?: string | null;
  userName?: string | null;

  // Nachrichten
  messages?: ChatMessage[] | null;

  // Letzte Nachricht (Vorschau)
  lastMessage?: string | null;
  lastMessageSenderId?: string | null;
  lastMessageTime?: number | null;

  // Ungelesene Nachrichten
  unreadCountOwner?: number | null;
  unreadCountUser?: number | null;

  // Status
  isActive?: boolean | null;
  [key: string]: any;
}

// ===================================================================
// Defensive Parser
// ===================================================================

function parseMessage(data: any): ChatMessage | null {
  if (!data || typeof data !== 'object') return null;
  return {
    ...data,
    id: data.id ?? null,
    senderId: data.senderId ?? null,
    senderName: data.senderName ?? null,
    content: data.content ?? null,
    timestamp: typeof data.timestamp === 'number' ? data.timestamp : null,
    read: data.read === true,
    readAt: typeof data.readAt === 'number' ? data.readAt : null,
  };
}

function parseChat(data: any): Chat | null {
  if (!data || typeof data !== 'object') return null;
  const rawMessages = Array.isArray(data.messages) ? data.messages : [];
  const messages = rawMessages
    .map(parseMessage)
    .filter((m: ChatMessage | null): m is ChatMessage => m !== null);
  return {
    ...data,
    id: data.id ?? null,
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : null,
    updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
    entryId: data.entryId ?? null,
    entryType: data.entryType ?? null,
    entryName: data.entryName ?? null,
    ownerId: data.ownerId ?? null,
    ownerName: data.ownerName ?? null,
    userId: data.userId ?? null,
    userName: data.userName ?? null,
    messages,
    lastMessage: data.lastMessage ?? null,
    lastMessageSenderId: data.lastMessageSenderId ?? null,
    lastMessageTime: typeof data.lastMessageTime === 'number' ? data.lastMessageTime : null,
    unreadCountOwner: typeof data.unreadCountOwner === 'number' ? data.unreadCountOwner : 0,
    unreadCountUser: typeof data.unreadCountUser === 'number' ? data.unreadCountUser : 0,
    isActive: data.isActive !== false,
  };
}

// ===================================================================
// Service
// ===================================================================

class ChatService {
  /**
   * Chat starten oder existierenden Chat laden.
   * POST /api/chat/start → Chat-Objekt (200) | Fehlertext (400)
   */
  async startChat(entryId: string, entryType: ChatEntryType): Promise<Chat | null> {
    const res = await api.post('/api/chat/start', { entryId, entryType });
    return parseChat(res?.data);
  }

  /**
   * Alle Chats des eingeloggten Users laden.
   * GET /api/chats → Chat[] (sortiert nach lastMessageTime absteigend)
   */
  async getMyChats(): Promise<Chat[]> {
    const res = await api.get('/api/chats');
    const list = Array.isArray(res?.data) ? res.data : [];
    return list
      .map(parseChat)
      .filter((c: Chat | null): c is Chat => c !== null);
  }

  /**
   * Einzelnen Chat laden.
   * GET /api/chat/:id → Chat (200) | 403 | 404
   */
  async getChatById(chatId: string): Promise<Chat | null> {
    const res = await api.get(`/api/chat/${encodeURIComponent(chatId)}`);
    return parseChat(res?.data);
  }

  /**
   * Nachricht senden.
   * POST /api/chat/:id/message → aktualisierter Chat
   */
  async sendMessage(chatId: string, content: string): Promise<Chat | null> {
    const res = await api.post(`/api/chat/${encodeURIComponent(chatId)}/message`, { content });
    void firstPartyAnalytics.trackEvent(AnalyticsEvents.messageSend, { kind: 'chat' });
    return parseChat(res?.data);
  }

  /**
   * Nachrichten als gelesen markieren.
   * POST /api/chat/:id/read → aktualisierter Chat
   */
  async markAsRead(chatId: string): Promise<Chat | null> {
    const res = await api.post(`/api/chat/${encodeURIComponent(chatId)}/read`, {});
    return parseChat(res?.data);
  }

  /**
   * Anzahl ungelesener Nachrichten über alle Chats.
   * GET /api/chats/unread-count → { unreadCount: number }
   */
  async getUnreadCount(): Promise<number> {
    try {
      const res = await api.get('/api/chats/unread-count');
      const count = res?.data?.unreadCount;
      return typeof count === 'number' ? count : 0;
    } catch (_e) {
      return 0;
    }
  }

  // =================================================================
  // Helper
  // =================================================================

  /** Ist der aktuelle User der Owner des Eintrags? */
  isOwner(chat?: Chat | null, currentUserId?: string | null): boolean {
    if (!chat || !currentUserId) return false;
    return chat.ownerId === currentUserId;
  }

  /**
   * User-ID des Gesprächspartners (für Profil-Links; der Name ist seit den
   * Live-Anzeigenamen der Anzeigename und kein Link-Ziel mehr).
   */
  getPartnerId(chat?: Chat | null, currentUserId?: string | null): string | null {
    if (!chat) return null;
    if (currentUserId && chat.ownerId === currentUserId) {
      return chat.userId ?? null;
    }
    return chat.ownerId ?? null;
  }

  /** Name des Gesprächspartners ermitteln */
  getPartnerName(chat?: Chat | null, currentUserId?: string | null): string {
    if (!chat) return t('web.component.comment-item.unbekannt');
    if (currentUserId && chat.ownerId === currentUserId) {
      return chat.userName || t('web.component.live-chat.benutzer');
    }
    return chat.ownerName || t('web.service.chat-service.veranstalter');
  }

  /** Ungelesene Nachrichten im Chat für den aktuellen User */
  getUnreadCountForUser(chat?: Chat | null, currentUserId?: string | null): number {
    if (!chat || !currentUserId) return 0;
    if (chat.ownerId === currentUserId) return chat.unreadCountOwner || 0;
    return chat.unreadCountUser || 0;
  }

  /** Vorschau der letzten Nachricht */
  getLastMessagePreview(chat?: Chat | null): string {
    if (!chat) return '';
    if (chat.lastMessage && chat.lastMessage.length > 0) return chat.lastMessage;
    const messages = chat.messages || [];
    const last = messages.length > 0 ? messages[messages.length - 1] : null;
    return last?.content || t('web.service.chat-service.noch-keine-nachrichten');
  }

  /** Deutsches Label für den Entry-Typ */
  getEntryTypeLabel(entryType?: string | null): string {
    switch (entryType) {
      case 'TOURNAMENT': return t('web.component.create-entry-modal.turnier');
      case 'CLUB': return t('web.component.create-entry-modal.verein');
      case 'PUB': return t('web.component.create-entry-modal.spielstaette');
      case 'SHOP': return t('web.component.create-entry-modal.shop');
      case 'SERIES': return t('web.component.create-entry-modal.turnierserie');
      case 'LEAGUE': return t('web.component.create-entry-modal.liga');
      case 'ASSOCIATION': return t('web.component.create-entry-modal.verband');
      case 'USER': return t('web.service.chat-service.direktnachricht');
      default: return t('web.page.favorites-page.eintrag');
    }
  }

  /** Uhrzeit einer Nachricht (HH:MM) */
  formatMessageTime(timestamp?: number | null): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  /** Datum einer Nachricht (TT.MM.JJJJ) */
  formatMessageDate(timestamp?: number | null): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  }

  /**
   * Zeit für die Konversationsliste:
   * heute → HH:MM | gestern → "Gestern" | sonst → TT.MM.JJJJ
   */
  formatConversationTime(timestamp?: number | null): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today.getTime() - messageDay.getTime()) / 86400000);
    if (diffDays === 0) return this.formatMessageTime(timestamp);
    if (diffDays === 1) return t('web.component.home-new-tournaments.gestern');
    return this.formatMessageDate(timestamp);
  }

  /**
   * Datums-Label für Tag-Trenner im Nachrichtenverlauf:
   * "Heute" | "Gestern" | TT.MM.JJJJ
   */
  formatDaySeparator(timestamp?: number | null): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today.getTime() - messageDay.getTime()) / 86400000);
    if (diffDays === 0) return t('web.component.home-live-pdc-lead.heute');
    if (diffDays === 1) return t('web.component.home-new-tournaments.gestern');
    return this.formatMessageDate(timestamp);
  }

  /** Fehlermeldung aus axios-Fehler extrahieren (Backend sendet Plaintext) */
  extractErrorMessage(error: any, fallback: string): string {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (typeof data?.message === 'string' && data.message.trim().length > 0) return data.message;
    return fallback;
  }
}

export default new ChatService();
